import { useEffect, useState, useCallback } from "react";
import { Rocket, Loader2, ExternalLink, Plus, Trash2, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { GitHubPanel } from "@/components/workspace/GitHubPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = { building: ["Compilando", "text-cyan-300"], live: ["Publicada", "text-emerald-300"], failed: ["Fallida", "text-red-300"] };

function EnvEditor({ project, onSaved }) {
  const [rows, setRows] = useState(Object.entries(project.env_vars || {}));
  const save = async () => {
    try {
      await api.put(`/projects/${project.id}/env`, { vars: Object.fromEntries(rows.filter(([k]) => k)) });
      toast.success("Variables guardadas. Se aplican en la próxima publicación."); onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="rounded-xl border border-[#222B40] p-4" data-testid="env-editor">
      <h3 className="text-white text-sm font-medium">Variables de entorno</h3>
      <p className="text-xs text-slate-500 mt-1">Disponibles en la app como window.APP_CONFIG. Son públicas: no guardes contraseñas ni claves secretas.</p>
      <div className="space-y-2 mt-3">
        {rows.map(([k, v], i) => (
          <div key={i} className="flex gap-2">
            <Input value={k} onChange={(e) => setRows(rows.map((r, j) => (j === i ? [e.target.value.toUpperCase(), r[1]] : r)))} placeholder="NOMBRE" className="h-8 font-mono text-xs bg-[#0A0D14]" data-testid={`env-key-${i}`} />
            <Input value={v} onChange={(e) => setRows(rows.map((r, j) => (j === i ? [r[0], e.target.value] : r)))} placeholder="valor" className="h-8 text-xs bg-[#0A0D14]" data-testid={`env-value-${i}`} />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-300" data-testid={`env-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setRows([...rows, ["", ""]])} className="text-xs px-3 py-1.5 rounded-lg border border-[#222B40] text-slate-300 inline-flex items-center gap-1" data-testid="env-add-btn"><Plus className="w-3 h-3" />Añadir</button>
        <button onClick={save} className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-400 text-white" data-testid="env-save-btn">Guardar</button>
      </div>
    </div>
  );
}

function StepCard({ n, title, children }) {
  return (
    <div className="rounded-xl border border-[#222B40] p-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-400">Paso {n}</p>
      <h3 className="text-white text-sm font-medium mt-1">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export function PublishView({ project, versions, onChanged, onPreview }) {
  const [providers, setProviders] = useState([]);
  const [deps, setDeps] = useState([]);
  const [busy, setBusy] = useState(null);
  const [ver, setVer] = useState(String(project.current_version || ""));
  const [report, setReport] = useState(null);
  const load = useCallback(() => { api.get(`/projects/${project.id}/deployments`).then((r) => setDeps(r.data)); }, [project.id]);
  useEffect(() => { api.get("/deploy/providers").then((r) => setProviders(r.data)); load(); }, [load]);
  useEffect(() => { setReport(versions.find((v) => String(v.number) === ver)?.quality || null); }, [ver, versions]);

  if (!versions.length) return <div className="p-8 text-sm text-slate-500">Genera tu aplicación antes de publicarla.</div>;

  const runTests = async () => {
    setBusy("verify");
    try { const { data } = await api.post(`/projects/${project.id}/versions/${ver}/verify`); setReport(data); toast[data.passed ? "success" : "error"](data.passed ? "Pruebas superadas" : "Las pruebas encontraron errores"); onChanged(); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(null);
  };
  const deploy = async (provider) => {
    setBusy(provider);
    try {
      const { data } = await api.post(`/projects/${project.id}/deploy`, { provider, version: Number(ver) });
      data.status === "live" ? toast.success("Publicación confirmada por el proveedor") : toast.error("La publicación falló. Revisa el registro.");
      load(); onChanged();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(null);
  };
  const unpublish = async () => { await api.post(`/projects/${project.id}/unpublish`); toast.success("Aplicación despublicada"); onChanged(); };

  return (
    <div className="h-full overflow-y-auto scroll-thin p-5 space-y-4" data-testid="publish-view">
      <div>
        <h3 className="text-white font-medium flex items-center gap-2"><Rocket className="w-4 h-4 text-cyan-400" />Agente Publicador</h3>
        <p className="text-xs text-slate-500 mt-1">Solo marca una app como publicada cuando el proveedor de despliegue lo confirma. Publicar cuesta créditos (compilación + despliegue).</p>
      </div>
      {project.published && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center justify-between gap-3" data-testid="publish-live-box">
          <div className="min-w-0"><p className="text-sm text-emerald-300">En línea · v{project.published_version}</p><p className="font-mono text-xs text-slate-400 truncate" data-testid="publish-live-url">{window.location.origin}/p/{project.slug}</p></div>
          <div className="flex gap-2 shrink-0">
            <a href={`/p/${project.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-[#222B40] text-slate-300" data-testid="publish-open-link"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={unpublish} className="text-xs px-3 rounded-lg border border-red-500/30 text-red-300" data-testid="publish-unpublish-btn">Despublicar</button>
          </div>
        </div>
      )}
      <StepCard n={1} title="Elige la versión a publicar">
        <div className="flex gap-2">
          <Select value={ver} onValueChange={setVer}>
            <SelectTrigger className="w-56 bg-[#0A0D14]" data-testid="publish-version-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161C2E] border-[#222B40]">
              {versions.map((v) => <SelectItem key={v.number} value={String(v.number)} data-testid={`publish-version-option-${v.number}`}>v{v.number}{v.number === project.current_version ? " (actual)" : ""}{v.number === project.published_version && project.published ? " · en línea" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => onPreview(Number(ver))} className="text-xs px-3 rounded-lg border border-[#222B40] text-slate-300 inline-flex items-center gap-1" data-testid="publish-preview-btn"><Eye className="w-3.5 h-3.5" />Vista previa</button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Publicar una versión anterior la restaura en tu dirección web pública.</p>
      </StepCard>
      <StepCard n={2} title="Ejecuta las pruebas">
        <div className="flex items-center gap-3">
          <button onClick={runTests} disabled={!!busy} data-testid="publish-run-tests-btn" className="text-xs px-3 py-2 rounded-lg border border-[#222B40] text-slate-200 inline-flex items-center gap-1.5">
            {busy === "verify" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}Ejecutar pruebas
          </button>
          {report && <span className={`text-xs ${report.passed ? "text-emerald-300" : "text-red-300"}`} data-testid="publish-tests-result">{report.passed ? `Superadas (${report.checks.filter((c) => c.ok).length}/${report.checks.length} comprobaciones)` : `${report.errors.length} error(es)`}</span>}
        </div>
        {report && !report.passed && report.errors.map((e, i) => <p key={i} className="text-[11px] text-red-200/80 mt-1">• {e}</p>)}
      </StepCard>
      <StepCard n={3} title="Publica">
        <div className="space-y-2">
          {providers.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-[#222B40] p-3" data-testid={`deploy-provider-${p.id}`}>
              <div><p className="text-sm text-white">{p.label}</p><p className={`text-xs ${p.configured ? "text-emerald-300" : "text-amber-300"}`}>{p.configured ? "Configurado" : "No configurado por el administrador"}</p></div>
              <button disabled={!p.configured || !!busy || report?.passed === false} onClick={() => deploy(p.id)} data-testid={`deploy-btn-${p.id}`}
                className="text-xs px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white inline-flex items-center gap-1.5">
                {busy === p.id && <Loader2 className="w-3 h-3 animate-spin" />}Publicar v{ver}
              </button>
            </div>
          ))}
        </div>
      </StepCard>
      <EnvEditor project={project} onSaved={onChanged} />
      <GitHubPanel project={project} version={Number(ver)} />
      <div>
        <h3 className="text-white text-sm font-medium mb-2">Estado y registro de despliegues</h3>
        {deps.length === 0 && <p className="text-xs text-slate-500">Sin despliegues.</p>}
        {deps.map((d) => (
          <div key={d.id} className="rounded-xl border border-[#222B40] p-3 mb-2" data-testid={`deployment-${d.id}`}>
            <div className="flex justify-between text-xs"><span className={STATUS[d.status]?.[1]} data-testid="deployment-status">{STATUS[d.status]?.[0] || d.status} · v{d.version} · {d.provider}</span><span className="text-slate-500">{fmtDate(d.created_at)}</span></div>
            {d.logs.map((l, i) => <p key={i} className="font-mono text-[11px] text-slate-400 mt-1">› {l.msg}</p>)}
          </div>
        ))}
      </div>
    </div>
  );
}
